import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, XCircle, Loader2, ClipboardCheck, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/constants";
import { useAuth } from "@/lib/auth";

function renderDecisionIcon(submitting, decision) {
  if (submitting) return <Loader2 className="h-4 w-4 mr-2 animate-spin" />;
  if (decision === "Approved") return <CheckCircle2 className="h-4 w-4 mr-2" />;
  return <XCircle className="h-4 w-4 mr-2" />;
}

export default function ApprovalQueueWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // {group, item, decision}
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "dept_head";

  const load = () => {
    setLoading(true);
    api.get("/dashboard/approval-queue?limit_per_module=10")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openDecision = (group, item, decision) => {
    setSelected({ group, item, decision });
    setNote("");
  };

  const submitDecision = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await api.post(
        `/modules/${selected.group.module_key}/records/${selected.item.id}/decision`,
        { decision: selected.decision, note: note || undefined }
      );
      toast.success(`${selected.decision}`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;
  if (!data) return null;

  const nonEmpty = data.groups.filter((g) => g.count > 0);

  return (
    <>
      <Card className="border-slate-200" data-testid="approval-queue-widget">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <ClipboardCheck className="h-3.5 w-3.5" /> Approval Queue
              </div>
              <div className="text-base font-semibold text-slate-900 mt-0.5">Pending your decision</div>
              {data.total_amount_pending > 0 && (
                <div className="text-xs text-slate-500 mt-1">
                  {formatCurrency(data.total_amount_pending)} in pending amounts
                </div>
              )}
            </div>
            <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50">
              {data.total_pending}
            </Badge>
          </div>

          {nonEmpty.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-md">
              <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
              You're all caught up. No pending approvals.
            </div>
          ) : (
            <div className="space-y-4 max-h-[440px] overflow-y-auto scrollbar-thin -mx-1 px-1">
              {nonEmpty.map((g) => (
                <div key={g.module_key} data-testid={`approval-group-${g.module_key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-700">{g.label}</span>
                      <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-[10px] px-1.5 py-0">
                        {g.count}
                      </Badge>
                    </div>
                    {g.amount_pending > 0 && (
                      <div className="text-[11px] text-slate-500 font-mono">
                        {formatCurrency(g.amount_pending)}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {g.items.slice(0, 5).map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-2 p-2.5 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors"
                        data-testid={`approval-item-${item.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{item.title}</div>
                          <div className="text-xs text-slate-500 truncate">{item.subtitle}</div>
                        </div>
                        {item.amount != null && (
                          <div className="text-sm font-mono font-semibold text-slate-900 whitespace-nowrap">
                            {formatCurrency(item.amount)}
                          </div>
                        )}
                        {isAdmin ? (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                              onClick={() => openDecision(g, item, "Approved")}
                              data-testid={`approve-${item.id}`}
                              title="Approve"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                              onClick={() => openDecision(g, item, "Rejected")}
                              data-testid={`reject-${item.id}`}
                              title="Reject"
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Pending</Badge>
                        )}
                      </div>
                    ))}
                    {g.count > 5 && (
                      <Link
                        to={`/m/${g.module_key}`}
                        className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 pl-2 pt-1"
                        data-testid={`approval-view-all-${g.module_key}`}
                      >
                        View all {g.count} pending → <ArrowUpRight className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent data-testid="approval-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {selected?.decision === "Approved" ? "Approve" : "Reject"} · {selected?.item?.title}
            </DialogTitle>
            <DialogDescription>
              {selected?.item?.subtitle}
              {selected?.item?.amount != null && (
                <span className="block mt-2 font-mono text-slate-900 font-semibold">
                  Amount: {formatCurrency(selected.item.amount)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Textarea
              placeholder={selected?.decision === "Rejected" ? "Reason for rejection (recommended)..." : "Optional note..."}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              data-testid="approval-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} data-testid="approval-cancel">Cancel</Button>
            <Button
              onClick={submitDecision}
              disabled={submitting}
              className={selected?.decision === "Approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="approval-confirm"
            >
              {renderDecisionIcon(submitting, selected?.decision)}
              Confirm {selected?.decision}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
